import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";

// Ported from Fluid Functionalism's shape-context (registry:lib). The class
// names below are defined in the "Surfaces" section of assets/css/theme.css.

/**
 * Radius for a surface inset inside another rounded surface.
 *
 * `inset` is the interior spacing (usually parent padding); pass the parent's
 * border separately as `borderWidth`. Together they form the visible
 * edge-to-edge distance. Clamping at zero prevents an invalid negative radius.
 */
function nestedRadius(outerRadius, inset, borderWidth = 0) {
  return Math.max(0, outerRadius - inset - borderWidth);
}

// bgRadius / containerRadius / mergedRadius are the numeric (px) counterparts
// of the classes, for places that animate individual corners. Popup shells use
// a 4px inset, so containerRadius = bgRadius + 4 keeps their curves concentric.
const shapeMap = {
  pill: {
    variant: "pill",
    item: "rounded-[20px]",
    bg: "rounded-[20px]",
    // +2px over `item` because the focus ring sits 2px outside the element
    // (top/left -2, width/height +4); this keeps the corners concentric so a
    // pill element gets a pill ring (matches the rounded-mode 8px→10px bump).
    focusRing: "rounded-[22px]",
    mergedBg: "rounded-2xl",
    container: "rounded-3xl",
    button: "rounded-[20px]",
    input: "rounded-[20px]",
    bgRadius: 20,
    containerRadius: 24,
    mergedRadius: 16,
  },
  rounded: {
    variant: "rounded",
    item: "rounded-lg",
    bg: "rounded-lg",
    focusRing: "rounded-[10px]",
    mergedBg: "rounded-lg",
    container: "rounded-xl",
    button: "rounded-lg",
    input: "rounded-lg",
    bgRadius: 8,
    containerRadius: 12,
    mergedRadius: 8,
  },
};

const ShapeContext = createContext(null);

// Rounded is the default on every path: a consumer with no provider gets the
// corners the docs show.
function useShape() {
  const ctx = useContext(ShapeContext);
  if (!ctx) return shapeMap.rounded;
  return ctx.classes;
}

function useShapeContext() {
  const ctx = useContext(ShapeContext);
  if (!ctx) throw new Error("useShapeContext must be used within a ShapeProvider");
  return ctx;
}

function ShapeProvider({ children, defaultShape = "rounded" }) {
  const [shape, setShapeState] = useState(defaultShape);
  const transitionTimeoutRef = useRef(null);

  // Run a state change under the `.transitioning` guard (added + reflow-flushed
  // first so the 180ms border-radius cross-fade applies). Clearing the previous
  // timeout first keeps a double-press from removing the class mid-fade.
  const transitionShape = useCallback((callback) => {
    const root = document.documentElement;
    root.classList.add("transitioning");
    void root.offsetHeight;
    callback();
    if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
    transitionTimeoutRef.current = setTimeout(
      () => root.classList.remove("transitioning"),
      200
    );
  }, []);

  const setShape = useCallback(
    (next) => {
      transitionShape(() => setShapeState(next));
    },
    [transitionShape]
  );

  // Publish the current element radius as a CSS custom property so plain-CSS
  // consumers that can't read React context stay in sync with the shape
  // system. Set on <html> so portalled content sees it too.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--shape-input-radius",
      `${shapeMap[shape].bgRadius}px`
    );
  }, [shape]);

  const value = useMemo(
    () => ({ shape, setShape, classes: shapeMap[shape] }),
    [shape, setShape]
  );

  return (
    <ShapeContext.Provider value={value}>{children}</ShapeContext.Provider>
  );
}

export { ShapeProvider, useShape, useShapeContext, shapeMap, nestedRadius };
